<script setup lang="ts">
import { Tag } from 'lucide-vue-next';
import TagInput from '@/components/entity/TagInput.vue';
import TagList from '@/components/entity/TagList.vue';
import ColorPickerButton from '@/components/ui/color-picker-button/ColorPickerButton.vue';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    normalizeEntityTagColor,
    normalizeEntityTagColors,
    normalizeEntityTags,
    resolveEntityTagColor,
    tagKey,
    type EntityTagColors,
} from '~/lib/entityTags';

const props = withDefaults(defineProps<{
    modelValue?: string[];
    suggestions?: string[];
    label?: string;
    description?: string;
    emptyLabel?: string;
    placeholder?: string;
    saveLabel?: string;
    cancelLabel?: string;
    limit?: number;
    compact?: boolean;
    disabled?: boolean;
    loading?: boolean;
    triggerClass?: string;
    contentClass?: string;
    colors?: EntityTagColors;
    allowColors?: boolean;
    colorsLabel?: string;
    save?: (tags: string[], colors: EntityTagColors) => Promise<void> | void;
}>(), {
    modelValue: () => [],
    suggestions: () => [],
    label: 'Tags',
    description: 'Add internal labels to organise this item.',
    emptyLabel: 'Add tags',
    placeholder: 'Type a tag',
    saveLabel: 'Save tags',
    cancelLabel: 'Cancel',
    limit: 3,
    compact: false,
    disabled: false,
    loading: false,
    triggerClass: '',
    contentClass: '',
    colors: () => ({}),
    allowColors: true,
    colorsLabel: 'Tag colours',
    save: undefined,
});

const emit = defineEmits<{
    'update:modelValue': [tags: string[]];
    saved: [tags: string[], colors: EntityTagColors];
}>();

const open = ref(false);
const draftTags = ref<string[]>([]);
const draftTagColors = ref<EntityTagColors>({});
const saving = ref(false);
const error = ref('');

const tags = computed(() => normalizeEntityTags(props.modelValue));
const normalizedDraftTags = computed(() => normalizeEntityTags(draftTags.value));
const colorsDirty = computed(() => normalizedDraftTags.value.some(tag => (
    resolveEntityTagColor(tag, draftTagColors.value) !== resolveEntityTagColor(tag, props.colors)
)));
const dirty = computed(() => (
    JSON.stringify(normalizedDraftTags.value) !== JSON.stringify(tags.value)
    || colorsDirty.value
));
const busy = computed(() => props.loading || saving.value);

function reset() {
    draftTags.value = [...tags.value];
    draftTagColors.value = Object.fromEntries(tags.value.map(tag => [
        tagKey(tag),
        resolveEntityTagColor(tag, props.colors),
    ]));
    error.value = '';
}

function handleOpenChange(value: boolean) {
    if (value && !props.disabled) reset();
    open.value = props.disabled ? false : value;
}

function setDraftTagColor(tag: string, value: string | number) {
    draftTagColors.value[tagKey(tag)] = normalizeEntityTagColor(value);
}

async function handleSave() {
    if (busy.value) return;
    const nextTags = normalizedDraftTags.value;
    const nextColors = normalizeEntityTagColors(Object.fromEntries(nextTags.map(tag => [
        tagKey(tag),
        resolveEntityTagColor(tag, draftTagColors.value),
    ])));

    if (!dirty.value) {
        open.value = false;
        return;
    }

    saving.value = true;
    error.value = '';
    try {
        await props.save?.(nextTags, nextColors);
        emit('update:modelValue', nextTags);
        emit('saved', nextTags, nextColors);
        open.value = false;
    } catch (saveError) {
        error.value = saveError instanceof Error ? saveError.message : 'Tags could not be saved.';
    } finally {
        saving.value = false;
    }
}

watch(() => props.modelValue, () => {
    if (!open.value) reset();
}, { deep: true, immediate: true });
</script>

<template>
    <Popover :open="open" @update:open="handleOpenChange">
        <PopoverTrigger as-child>
            <div
                :class="[
                    'inline-flex min-w-0 items-center gap-1.5 rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    disabled ? 'cursor-default' : 'cursor-pointer hover:bg-muted/60',
                    triggerClass,
                ]"
                :aria-label="disabled ? label : `Edit ${label.toLowerCase()}`"
                :aria-disabled="disabled"
                :tabindex="disabled ? -1 : 0"
                @click.stop
            >
                <slot name="trigger" :tags="tags" :disabled="disabled">
                    <TagList v-if="tags.length" :tags="tags" :colors="colors" :limit="limit" :compact="compact" />
                    <span v-else class="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Tag class="size-3.5" />
                        {{ emptyLabel }}
                    </span>
                </slot>
            </div>
        </PopoverTrigger>

        <PopoverContent
            :class="['max-h-[min(36rem,calc(100vh-2rem))] w-[min(22rem,calc(100vw-2rem))] space-y-4 overflow-y-auto p-4', contentClass]"
            align="start"
            @click.stop
            @open-auto-focus.prevent
        >
            <div>
                <p class="text-sm font-semibold">{{ label }}</p>
                <p v-if="description" class="mt-1 text-xs leading-5 text-muted-foreground">{{ description }}</p>
            </div>

            <TagInput
                v-model="draftTags"
                :suggestions="suggestions"
                :placeholder="placeholder"
                :disabled="busy"
                :loading="busy"
            />

            <div v-if="allowColors && draftTags.length" class="space-y-2 border-t pt-3">
                <p class="text-xs font-medium text-muted-foreground">{{ colorsLabel }}</p>
                <div
                    v-for="tag in normalizedDraftTags"
                    :key="tagKey(tag)"
                    class="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-b-0"
                >
                    <TagList :tags="[tag]" :colors="draftTagColors" :limit="1" compact />
                    <ColorPickerButton
                        :model-value="resolveEntityTagColor(tag, draftTagColors)"
                        :label="`${tag} colour`"
                        :disabled="busy"
                        @update:model-value="value => setDraftTagColor(tag, value)"
                    />
                </div>
            </div>

            <p v-if="error" class="text-xs text-destructive" role="alert">{{ error }}</p>

            <div class="flex justify-end gap-2 border-t pt-3">
                <Button type="button" variant="ghost" size="sm" :disabled="busy" @click="open = false">
                    {{ cancelLabel }}
                </Button>
                <Button type="button" size="sm" :disabled="busy || !dirty" @click="handleSave">
                    {{ saving ? 'Saving…' : saveLabel }}
                </Button>
            </div>
        </PopoverContent>
    </Popover>
</template>
